import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { MainLayout } from '../../components/layout';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Badge from '../../components/ui/Badge';
import Modal from '../../components/ui/Modal';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import Loading from '../../components/ui/Loading';
import Alert from '../../components/ui/Alert';
import FaceMatchingStatus from '../../components/persons/FaceMatchingStatus';
import PersonMatchHistory from '../../components/persons/PersonMatchHistory';
import {
  ArrowLeft,
  Edit2,
  User,
  Camera,
  MapPin,
  Car,
  Upload,
  Trash2,
  Plus,
  Star,
  AlertTriangle,
  Clock,
  Phone,
  Mail,
  Briefcase,
  Calendar,
  FileText,
  X,
} from 'lucide-react';
import { personService, locationService } from '../../services';
import type {
  PersonDetail,
  FaceImage,
  PersonLocation,
  PersonVehicle,
  PersonType,
  Location,
  CreatePersonLocationData,
  CreatePersonVehicleData,
  VehicleType,
} from '../../types';

type TabType = 'info' | 'photos' | 'locations' | 'vehicles';

const PERSON_TYPE_CONFIG: Record<PersonType, { label: string; color: string }> = {
  EMPLOYEE: { label: 'Employee', color: 'bg-blue-500/10 text-blue-500' },
  SUBCONTRACTOR: { label: 'Subcontractor', color: 'bg-purple-500/10 text-purple-500' },
  VISITOR: { label: 'Visitor', color: 'bg-green-500/10 text-green-500' },
};

const ACCESS_LEVEL_CONFIG: Record<string, { label: string; color: string }> = {
  FULL: { label: 'Full Access', color: 'bg-green-500' },
  LIMITED: { label: 'Limited', color: 'bg-yellow-500' },
  ESCORT_REQUIRED: { label: 'Escort Required', color: 'bg-orange-500' },
  NONE: { label: 'No Access', color: 'bg-red-500' },
};

const ViewPersonPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Data state
  const [person, setPerson] = useState<PersonDetail | null>(null);
  const [allLocations, setAllLocations] = useState<Location[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  
  // Tab state
  const [activeTab, setActiveTab] = useState<TabType>('info');
  
  // Photo upload state
  const [isUploading, setIsUploading] = useState(false);
  const [uploadAsPrimary, setUploadAsPrimary] = useState(false);
  
  // Delete photo modal
  const [photoToDelete, setPhotoToDelete] = useState<FaceImage | null>(null);
  
  // Add location modal
  const [isAddLocationOpen, setIsAddLocationOpen] = useState(false);
  const [newLocationData, setNewLocationData] = useState<CreatePersonLocationData>({
    location_id: 0,
    access_type: 'ALLOW',
    valid_from: '',
    valid_until: '',
    notes: '',
  });
  const [isAddingLocation, setIsAddingLocation] = useState(false);
  
  // Delete location modal
  const [locationToRevoke, setLocationToRevoke] = useState<PersonLocation | null>(null);
  
  // Add vehicle modal
  const [isAddVehicleOpen, setIsAddVehicleOpen] = useState(false);
  const [newVehicleData, setNewVehicleData] = useState<CreatePersonVehicleData>({
    plate_number: '',
    plate_state: '',
    vehicle_make: '',
    vehicle_model: '',
    vehicle_color: '',
    vehicle_year: undefined,
    vehicle_type: 'CAR',
    is_primary: false,
  });
  const [isAddingVehicle, setIsAddingVehicle] = useState(false);
  
  // Delete vehicle modal
  const [vehicleToRemove, setVehicleToRemove] = useState<PersonVehicle | null>(null);

  // Fetch person detail
  const fetchPerson = useCallback(async () => {
    if (!id) return;
    try {
      setIsLoading(true);
      setError(null);
      const data = await personService.getPersonDetail(parseInt(id));
      setPerson(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch person');
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchPerson();
  }, [fetchPerson]);

  // Fetch all locations for add location modal
  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const response = await locationService.getLocations();
        setAllLocations(response.data || []);
      } catch (err) {
        console.error('Failed to fetch locations:', err);
      }
    };
    fetchLocations();
  }, []);

  // Clear messages
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  // Photo handlers
  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !id) return;

    const validTypes = ['image/jpeg', 'image/png'];
    if (!validTypes.includes(file.type)) {
      setError('Please upload a JPG or PNG image');
      return;
    }

    try {
      setIsUploading(true);
      await personService.uploadFaceImage(parseInt(id), file, uploadAsPrimary);
      setSuccessMessage('Photo uploaded successfully');
      setUploadAsPrimary(false);
      fetchPerson();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to upload photo');
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDeletePhoto = async () => {
    if (!photoToDelete || !id) return;
    try {
      await personService.deleteFaceImage(parseInt(id), photoToDelete.id);
      setSuccessMessage('Photo deleted successfully');
      setPhotoToDelete(null);
      fetchPerson();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete photo');
    }
  };

  // Location handlers
  const handleAddLocation = async () => {
    if (!id || !newLocationData.location_id) return;
    try {
      setIsAddingLocation(true);
      await personService.grantLocationAccess(parseInt(id), {
        ...newLocationData,
        valid_from: newLocationData.valid_from ? `${newLocationData.valid_from}T00:00:00` : undefined,
        valid_until: newLocationData.valid_until ? `${newLocationData.valid_until}T23:59:59` : undefined,
      });
      setSuccessMessage('Location access granted');
      setIsAddLocationOpen(false);
      setNewLocationData({ location_id: 0, access_type: 'ALLOW', valid_from: '', valid_until: '', notes: '' });
      fetchPerson();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to grant location access');
    } finally {
      setIsAddingLocation(false);
    }
  };

  const handleRevokeLocation = async () => {
    if (!locationToRevoke || !id) return;
    try {
      await personService.revokeLocationAccess(parseInt(id), locationToRevoke.location_id);
      setSuccessMessage('Location access revoked');
      setLocationToRevoke(null);
      fetchPerson();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to revoke location access');
    }
  };

  // Vehicle handlers
  const handleAddVehicle = async () => {
    if (!id || !newVehicleData.plate_number.trim()) {
      setError('Plate number is required');
      return;
    }
    try {
      setIsAddingVehicle(true);
      await personService.addVehicle(parseInt(id), newVehicleData);
      setSuccessMessage('Vehicle added successfully');
      setIsAddVehicleOpen(false);
      setNewVehicleData({
        plate_number: '',
        plate_state: '',
        vehicle_make: '',
        vehicle_model: '',
        vehicle_color: '',
        vehicle_year: undefined,
        vehicle_type: 'CAR',
        is_primary: false,
      });
      fetchPerson();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add vehicle');
    } finally {
      setIsAddingVehicle(false);
    }
  };

  const handleRemoveVehicle = async () => {
    if (!vehicleToRemove || !id) return;
    try {
      await personService.removeVehicle(parseInt(id), vehicleToRemove.id);
      setSuccessMessage('Vehicle removed successfully');
      setVehicleToRemove(null);
      fetchPerson();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove vehicle');
    }
  };

  // Utility functions
  const isExpired = (validUntil?: string) => {
    if (!validUntil) return false;
    return new Date(validUntil) < new Date();
  };

  const isExpiringSoon = (validUntil?: string) => {
    if (!validUntil) return false;
    const expiry = new Date(validUntil);
    const now = new Date();
    const sevenDays = 7 * 24 * 60 * 60 * 1000;
    return expiry > now && expiry.getTime() - now.getTime() < sevenDays;
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString();
  };

  // Available locations (not already granted)
  const availableLocations = allLocations.filter(
    loc => !person?.locations.some(pl => pl.location_id === loc.ycl_id)
  );

  const vehicleTypeOptions = [
    { label: 'Car', value: 'CAR' },
    { label: 'Truck', value: 'TRUCK' },
    { label: 'SUV', value: 'SUV' },
    { label: 'Van', value: 'VAN' },
    { label: 'Motorcycle', value: 'MOTORCYCLE' },
    { label: 'Other', value: 'OTHER' },
  ];

  const tabs = [
    { key: 'info' as TabType, label: 'Info', icon: <User size={16} /> },
    { key: 'photos' as TabType, label: 'Photos', icon: <Camera size={16} />, count: person?.face_images?.length },
    { key: 'locations' as TabType, label: 'Locations', icon: <MapPin size={16} />, count: person?.locations?.length },
    { key: 'vehicles' as TabType, label: 'Vehicles', icon: <Car size={16} />, count: person?.vehicles?.length },
  ];

  if (isLoading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center py-20">
          <Loading size="lg" text="Loading person..." />
        </div>
      </MainLayout>
    );
  }

  if (!person) {
    return (
      <MainLayout>
        <Alert variant="error" message="Person not found" />
      </MainLayout>
    );
  }

  const typeConfig = PERSON_TYPE_CONFIG[person.person_type];
  const accessConfig = ACCESS_LEVEL_CONFIG[person.access_level];

  return (
    <MainLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              leftIcon={<ArrowLeft size={18} />}
              onClick={() => navigate('/persons')}
            >
              Back
            </Button>
            <div className="flex items-center gap-4">
              {/* Primary Photo */}
              <div className="w-16 h-16 rounded-full bg-surface-secondary overflow-hidden flex items-center justify-center">
                {person.primary_photo_url ? (
                  <img
                    src={person.primary_photo_url}
                    alt={`${person.first_name} ${person.last_name}`}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <User size={32} className="text-text-muted" />
                )}
              </div>
              <div>
                <div className="flex items-center gap-3">
                  <h1 className="text-2xl font-bold text-text-primary">
                    {person.first_name} {person.last_name}
                  </h1>
                  <div className={`px-2 py-1 rounded-full text-xs font-medium ${typeConfig.color}`}>
                    {typeConfig.label}
                  </div>
                  {!person.is_active && <Badge variant="danger">Inactive</Badge>}
                  {isExpired(person.valid_until) && (
                    <Badge variant="danger" className="flex items-center gap-1">
                      <AlertTriangle size={10} /> Expired
                    </Badge>
                  )}
                  {isExpiringSoon(person.valid_until) && !isExpired(person.valid_until) && (
                    <Badge variant="warning" className="flex items-center gap-1">
                      <Clock size={10} /> Expiring Soon
                    </Badge>
                  )}
                </div>
                <div className="text-sm text-text-muted mt-1">
                  {person.employee_id && <span>ID: {person.employee_id} • </span>}
                  {person.external_company && <span>{person.external_company} • </span>}
                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs ${accessConfig.color} text-white`}>
                    {accessConfig.label}
                  </span>
                </div>
              </div>
            </div>
          </div>
          <Button
            onClick={() => navigate(`/persons/${id}/edit`)}
            leftIcon={<Edit2 size={18} />}
          >
            Edit Person
          </Button>
        </div>

        {/* Alerts */}
        {error && <Alert variant="error" message={error} onClose={() => setError(null)} />}
        {successMessage && <Alert variant="success" message={successMessage} onClose={() => setSuccessMessage(null)} />}

        {/* Tabs */}
        <div className="border-b border-surface-secondary">
          <nav className="-mb-px flex space-x-6">
            {tabs.map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors flex items-center gap-2 ${
                  activeTab === tab.key
                    ? 'border-accent-primary text-accent-primary'
                    : 'border-transparent text-text-muted hover:text-text-primary hover:border-surface-hover'
                }`}
              >
                {tab.icon}
                {tab.label}
                {tab.count !== undefined && tab.count > 0 && (
                  <span className="bg-surface-secondary px-1.5 py-0.5 rounded text-xs">
                    {tab.count}
                  </span>
                )}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        {activeTab === 'info' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Contact Info */}
            <Card padding="lg">
              <h3 className="text-lg font-semibold text-text-primary mb-4">Contact Information</h3>
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <Phone size={18} className="text-text-muted" />
                  <span className="text-text-primary">{person.phone || 'No phone'}</span>
                </div>
                <div className="flex items-center gap-3">
                  <Mail size={18} className="text-text-muted" />
                  <span className="text-text-primary">{person.email || 'No email'}</span>
                </div>
                {person.external_company && (
                  <div className="flex items-center gap-3">
                    <Briefcase size={18} className="text-text-muted" />
                    <span className="text-text-primary">{person.external_company}</span>
                  </div>
                )}
              </div>
            </Card>

            {/* Access Period */}
            <Card padding="lg">
              <h3 className="text-lg font-semibold text-text-primary mb-4">Access Period</h3>
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <Calendar size={18} className="text-text-muted" />
                  <div>
                    <span className="text-text-muted">Valid From: </span>
                    <span className="text-text-primary">{formatDate(person.valid_from)}</span>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Calendar size={18} className="text-text-muted" />
                  <div>
                    <span className="text-text-muted">Valid Until: </span>
                    <span className={`${isExpired(person.valid_until) ? 'text-red-500' : isExpiringSoon(person.valid_until) ? 'text-yellow-500' : 'text-text-primary'}`}>
                      {person.valid_until ? formatDate(person.valid_until) : 'Permanent'}
                    </span>
                  </div>
                </div>
              </div>
            </Card>

            {/* Notes */}
            {person.notes && (
              <Card padding="lg" className="lg:col-span-2">
                <h3 className="text-lg font-semibold text-text-primary mb-4 flex items-center gap-2">
                  <FileText size={18} />
                  Notes
                </h3>
                <p className="text-text-secondary whitespace-pre-wrap">{person.notes}</p>
              </Card>
            )}
          </div>
        )}

        {activeTab === 'photos' && (
          <div className="space-y-4">
            <FaceMatchingStatus />
            <PersonMatchHistory personId={person.id} />
            {/* Upload Section */}
            <Card padding="md">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-medium text-text-primary">Face Photos</h3>
                  <p className="text-sm text-text-muted">Upload clear photos for this person’s profile</p>
                </div>
                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={uploadAsPrimary}
                      onChange={(e) => setUploadAsPrimary(e.target.checked)}
                      className="rounded"
                    />
                    <span className="text-text-secondary">Set as primary</span>
                  </label>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png"
                    onChange={handlePhotoUpload}
                    className="hidden"
                  />
                  <Button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isUploading}
                    leftIcon={isUploading ? undefined : <Upload size={18} />}
                  >
                    {isUploading ? 'Uploading...' : 'Upload Photo'}
                  </Button>
                </div>
              </div>
            </Card>

            {/* Photos Grid */}
            {person.face_images.length === 0 ? (
              <Card padding="lg">
                <div className="text-center py-8">
                  <Camera size={48} className="mx-auto text-text-muted mb-4" />
                  <p className="text-text-muted">No photos uploaded yet</p>
                </div>
              </Card>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                {person.face_images.map(image => (
                  <div
                    key={image.id}
                    className="relative aspect-square rounded-lg overflow-hidden bg-surface-secondary group"
                  >
                    <img
                      src={image.image_url}
                      alt="Face"
                      className="w-full h-full object-cover"
                    />
                    {image.is_primary && (
                      <div className="absolute top-2 left-2 bg-yellow-500 text-white px-2 py-0.5 rounded text-xs flex items-center gap-1">
                        <Star size={10} /> Primary
                      </div>
                    )}
                    <button
                      onClick={() => setPhotoToDelete(image)}
                      className="absolute top-2 right-2 bg-red-500 text-white p-1.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'locations' && (
          <div className="space-y-4">
            {/* Add Location Button */}
            <div className="flex justify-end">
              <Button
                onClick={() => setIsAddLocationOpen(true)}
                leftIcon={<Plus size={18} />}
                disabled={availableLocations.length === 0}
              >
                Grant Location Access
              </Button>
            </div>

            {/* Locations List */}
            {person.locations.length === 0 ? (
              <Card padding="lg">
                <div className="text-center py-8">
                  <MapPin size={48} className="mx-auto text-text-muted mb-4" />
                  <p className="text-text-muted">No location access granted</p>
                </div>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {person.locations.map(loc => (
                  <Card key={loc.id} padding="md">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-lg bg-accent-primary/10 flex items-center justify-center">
                          <MapPin size={20} className="text-accent-primary" />
                        </div>
                        <div>
                          <div className="font-medium text-text-primary">{loc.location_name || `Location #${loc.location_id}`}</div>
                          <Badge variant={loc.access_type === 'ALLOW' ? 'success' : 'danger'} size="sm">
                            {loc.access_type}
                          </Badge>
                          {loc.valid_until && (
                            <div className="text-xs text-text-muted mt-1">
                              Until: {formatDate(loc.valid_until)}
                            </div>
                          )}
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setLocationToRevoke(loc)}
                        className="text-red-500"
                      >
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'vehicles' && (
          <div className="space-y-4">
            {/* Add Vehicle Button */}
            <div className="flex justify-end">
              <Button onClick={() => setIsAddVehicleOpen(true)} leftIcon={<Plus size={18} />}>
                Add Vehicle
              </Button>
            </div>

            {/* Vehicles List */}
            {person.vehicles.length === 0 ? (
              <Card padding="lg">
                <div className="text-center py-8">
                  <Car size={48} className="mx-auto text-text-muted mb-4" />
                  <p className="text-text-muted">No vehicles registered</p>
                </div>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {person.vehicles.map(vehicle => (
                  <Card key={vehicle.id} padding="md">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                          <Car size={20} className="text-blue-500" />
                        </div>
                        <div>
                          <div className="font-bold text-text-primary text-lg font-mono flex items-center gap-2">
                            {vehicle.plate_number}
                            {vehicle.is_primary && (
                              <span className="text-yellow-500"><Star size={14} /></span>
                            )}
                          </div>
                          <div className="text-sm text-text-secondary">
                            {[vehicle.vehicle_color, vehicle.vehicle_year, vehicle.vehicle_make, vehicle.vehicle_model]
                              .filter(Boolean)
                              .join(' ')}
                          </div>
                          <div className="text-xs text-text-muted mt-1">
                            {vehicle.vehicle_type} • {vehicle.plate_state || 'Unknown State'}
                          </div>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setVehicleToRemove(vehicle)}
                        className="text-red-500"
                      >
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Delete Photo Modal */}
        <Modal
          isOpen={!!photoToDelete}
          onClose={() => setPhotoToDelete(null)}
          title="Delete Photo"
        >
          <div className="space-y-4">
            <p className="text-text-secondary">Are you sure you want to delete this photo?</p>
            <div className="flex justify-end gap-3">
              <Button variant="secondary" onClick={() => setPhotoToDelete(null)}>Cancel</Button>
              <Button variant="danger" onClick={handleDeletePhoto}>Delete</Button>
            </div>
          </div>
        </Modal>

        {/* Add Location Modal */}
        <Modal
          isOpen={isAddLocationOpen}
          onClose={() => setIsAddLocationOpen(false)}
          title="Grant Location Access"
        >
          <div className="space-y-4">
            <Select
              label="Location"
              value={newLocationData.location_id.toString()}
              onChange={(e) => setNewLocationData(prev => ({ ...prev, location_id: parseInt(e.target.value) }))}
              options={[
                { label: 'Select a location...', value: '0' },
                ...availableLocations.map(loc => ({ label: loc.ycl_name, value: loc.ycl_id.toString() })),
              ]}
            />
            <Select
              label="Access Type"
              value={newLocationData.access_type || 'ALLOW'}
              onChange={(e) => setNewLocationData(prev => ({ ...prev, access_type: e.target.value as 'ALLOW' | 'DENY' }))}
              options={[
                { label: 'Allow', value: 'ALLOW' },
                { label: 'Deny', value: 'DENY' },
              ]}
            />
            <Input
              label="Valid Until (optional)"
              type="date"
              value={newLocationData.valid_until || ''}
              onChange={(e) => setNewLocationData(prev => ({ ...prev, valid_until: e.target.value }))}
            />
            <div className="flex justify-end gap-3">
              <Button variant="secondary" onClick={() => setIsAddLocationOpen(false)}>Cancel</Button>
              <Button onClick={handleAddLocation} disabled={isAddingLocation || !newLocationData.location_id}>
                {isAddingLocation ? 'Adding...' : 'Grant Access'}
              </Button>
            </div>
          </div>
        </Modal>

        {/* Revoke Location Modal */}
        <Modal
          isOpen={!!locationToRevoke}
          onClose={() => setLocationToRevoke(null)}
          title="Revoke Location Access"
        >
          <div className="space-y-4">
            <p className="text-text-secondary">
              Are you sure you want to revoke access to <strong>{locationToRevoke?.location_name}</strong>?
            </p>
            <div className="flex justify-end gap-3">
              <Button variant="secondary" onClick={() => setLocationToRevoke(null)}>Cancel</Button>
              <Button variant="danger" onClick={handleRevokeLocation}>Revoke Access</Button>
            </div>
          </div>
        </Modal>

        {/* Add Vehicle Modal */}
        <Modal
          isOpen={isAddVehicleOpen}
          onClose={() => setIsAddVehicleOpen(false)}
          title="Add Vehicle"
        >
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <Input
                label="Plate Number"
                value={newVehicleData.plate_number}
                onChange={(e) => setNewVehicleData(prev => ({ ...prev, plate_number: e.target.value.toUpperCase() }))}
                placeholder="ABC1234"
                required
              />
              <Input
                label="State/Province"
                value={newVehicleData.plate_state || ''}
                onChange={(e) => setNewVehicleData(prev => ({ ...prev, plate_state: e.target.value }))}
                placeholder="ON"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Input
                label="Make"
                value={newVehicleData.vehicle_make || ''}
                onChange={(e) => setNewVehicleData(prev => ({ ...prev, vehicle_make: e.target.value }))}
                placeholder="Ford"
              />
              <Input
                label="Model"
                value={newVehicleData.vehicle_model || ''}
                onChange={(e) => setNewVehicleData(prev => ({ ...prev, vehicle_model: e.target.value }))}
                placeholder="F-150"
              />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <Input
                label="Color"
                value={newVehicleData.vehicle_color || ''}
                onChange={(e) => setNewVehicleData(prev => ({ ...prev, vehicle_color: e.target.value }))}
                placeholder="White"
              />
              <Input
                label="Year"
                type="number"
                value={newVehicleData.vehicle_year?.toString() || ''}
                onChange={(e) => setNewVehicleData(prev => ({ ...prev, vehicle_year: e.target.value ? parseInt(e.target.value) : undefined }))}
                placeholder="2024"
              />
              <Select
                label="Type"
                value={newVehicleData.vehicle_type || 'CAR'}
                onChange={(e) => setNewVehicleData(prev => ({ ...prev, vehicle_type: e.target.value as VehicleType }))}
                options={vehicleTypeOptions}
              />
            </div>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={newVehicleData.is_primary || false}
                onChange={(e) => setNewVehicleData(prev => ({ ...prev, is_primary: e.target.checked }))}
                className="rounded"
              />
              <span className="text-text-secondary">Primary vehicle</span>
            </label>
            <div className="flex justify-end gap-3">
              <Button variant="secondary" onClick={() => setIsAddVehicleOpen(false)}>Cancel</Button>
              <Button onClick={handleAddVehicle} disabled={isAddingVehicle || !newVehicleData.plate_number}>
                {isAddingVehicle ? 'Adding...' : 'Add Vehicle'}
              </Button>
            </div>
          </div>
        </Modal>

        {/* Remove Vehicle Modal */}
        <Modal
          isOpen={!!vehicleToRemove}
          onClose={() => setVehicleToRemove(null)}
          title="Remove Vehicle"
        >
          <div className="space-y-4">
            <p className="text-text-secondary">
              Are you sure you want to remove vehicle <strong>{vehicleToRemove?.plate_number}</strong>?
            </p>
            <div className="flex justify-end gap-3">
              <Button variant="secondary" onClick={() => setVehicleToRemove(null)}>Cancel</Button>
              <Button variant="danger" onClick={handleRemoveVehicle}>Remove</Button>
            </div>
          </div>
        </Modal>
      </div>
    </MainLayout>
  );
};

export default ViewPersonPage;
